import React from 'react';
import Icon from '../../../components/AppIcon';

const AIAnalysisSummary = ({ analysis, currentScore, latestScores }) => {
  const getReadinessColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-[#1FB5A5]';
    if (score >= 40) return 'text-yellow-400';
    return 'text-orange-400';
  };

  return (
    <div className="mb-8">
      <div className="bg-white rounded-xl shadow-elevation-2 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <Icon name="Brain" size={20} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#002B45]">AI Analysis Summary</h2>
            <p className="text-sm text-gray-500">Data maturity profile and organizational context</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Maturity Level */}
          <div className="bg-gradient-to-br from-[#002B45] to-[#003D5C] rounded-lg p-6 text-white">
            <p className="text-gray-300 text-sm mb-2">Maturity Classification</p>
            <div className="text-2xl font-bold mb-2">{analysis?.maturityLevel}</div>
            <div className={`text-4xl font-bold mb-3 ${getReadinessColor(currentScore)}`}>
              {currentScore}/100
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Icon name="Target" size={16} className="text-[#1FB5A5]" />
              <span className="text-gray-300">Readiness Score: {analysis?.readinessScore}/100</span>
            </div>
          </div>

          {/* Key Strengths */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <h3 className="font-bold text-green-900">Key Strengths</h3>
            </div>
            <ul className="space-y-2">
              {analysis?.strengths?.map((strength, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-green-700">
                  <Icon name="TrendingUp" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Critical Gaps */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="AlertTriangle" size={20} className="text-orange-600" />
              <h3 className="font-bold text-orange-900">Critical Gaps</h3>
            </div>
            <ul className="space-y-2">
              {analysis?.gaps?.map((gap, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-orange-700">
                  <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{gap}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Focus Areas */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Target" size={20} className="text-blue-600" />
            <h3 className="font-bold text-blue-900">Recommended Focus Areas (Next 6-12 Months)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {analysis?.focusAreas?.map((area, idx) => (
              <div key={idx} className="flex items-start gap-2 bg-white rounded-lg p-3">
                <Icon name="ArrowRight" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-blue-700">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisSummary;